import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Auth/auth.css';
import { API_BASE } from './config/api';

const Payment = () => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const verified = sp.get('verified');
    const email = sp.get('email');
    
    if (verified === '1' && email) {
      setUserEmail(email);
      setStatusMessage({ 
        text: 'Email verified. Continue to payment.', 
        type: 'success' 
      });
      
      // Retrieve selected plan from localStorage
      const storedPlan = localStorage.getItem('selectedPlan');
      if (storedPlan) {
        setSelectedPlan(JSON.parse(storedPlan));
      } else {
        // If no plan stored, redirect to signup to select one
        setStatusMessage({ 
          text: 'Please select a plan first.', 
          type: 'error' 
        });
        setTimeout(() => {
          navigate('/signup');
        }, 2000);
      }
    } else {
      // If not verified, redirect to login
      navigate('/login?verified=0');
    }
  }, [location, navigate]);

  const handlePayment = async () => {
    if (!selectedPlan?.stripePriceId) {
      setStatusMessage({ 
        text: 'Payment configuration error. Please contact support.', 
        type: 'error' 
      });
      return;
    }

    setLoading(true);
    setStatusMessage({ text: '', type: '' });
    
    try {
      // Create Stripe checkout session directly using email
      const response = await fetch(`${API_BASE}/create-pending-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          priceId: selectedPlan.stripePriceId,
          planId: selectedPlan.planId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Payment setup failed');
      }

      // Clear stored plan and redirect to Stripe Checkout
      localStorage.removeItem('selectedPlan');
      window.location.href = data.url;
    } catch (err) {
      setStatusMessage({ 
        text: err.message || 'Payment failed', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDifferentPlan = () => {
    localStorage.removeItem('selectedPlan');
    navigate('/signup');
  };

  if (!selectedPlan) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ 
            margin: '0 auto 16px',
            width: '40px',
            height: '40px',
            border: '4px solid rgba(96, 165, 250, 0.2)',
            borderTop: '4px solid #60a5fa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#9ca3af' }}>Loading payment information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h1>Complete Your Payment</h1>
        
        {statusMessage.text && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            background: statusMessage.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 
                       statusMessage.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 
                       'rgba(96, 165, 250, 0.1)',
            border: `1px solid ${statusMessage.type === 'error' ? 'rgba(239, 68, 68, 0.3)' : 
                                 statusMessage.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 
                                 'rgba(96, 165, 250, 0.3)'}`,
            color: statusMessage.type === 'error' ? '#ef4444' : 
                   statusMessage.type === 'success' ? '#22c55e' : 
                   '#60a5fa',
            fontSize: '14px'
          }}>
            {statusMessage.text}
          </div>
        )}

        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: '8px',
          background: 'rgba(96, 165, 250, 0.1)',
          border: '1px solid rgba(96, 165, 250, 0.3)'
        }}>
          <p style={{ color: '#60a5fa', fontSize: '14px', margin: 0 }}>
            Complete your payment to activate your plan.
          </p>
        </div>

        <div style={{
          padding: '20px',
          marginBottom: '20px',
          borderRadius: '8px',
          background: '#0f172a',
          border: '1px solid #334155'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#e5e7eb' }}>Order Summary</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ color: '#9ca3af' }}>Email:</span>
            <span style={{ color: '#e5e7eb' }}>{userEmail}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ color: '#9ca3af' }}>Plan:</span>
            <span style={{ color: '#e5e7eb', fontWeight: '600' }}>{selectedPlan.planName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ color: '#9ca3af' }}>Price:</span>
            <span style={{ color: '#e5e7eb', fontWeight: '600' }}>£{selectedPlan.monthlyPrice}/month</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            borderTop: '1px solid #334155', 
            paddingTop: '10px',
            marginTop: '10px'
          }}>
            <span style={{ color: '#e5e7eb', fontWeight: '600' }}>Total:</span>
            <span style={{ color: '#60a5fa', fontWeight: '700', fontSize: '18px' }}>£{selectedPlan.monthlyPrice}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button 
            className="button"
            onClick={handlePayment}
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Processing...' : 'Pay with Stripe'}
          </button>
          
          <button 
            className="button"
            onClick={handleSelectDifferentPlan}
            disabled={loading}
            style={{ 
              width: '100%',
              background: 'transparent',
              border: '1px solid #334155',
              color: '#9ca3af'
            }}
          >
            Select Different Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default Payment;

